import { useSyncExternalStore } from "react";
import { forgetUpdatePromptDismissed } from "./updatePrompt.ts";

/**
 * "There is a new version of the app, and it is waiting for you."
 *
 * Ported from the in-game app (platform-2.0 features/loading, client
 * 2026-10-07). The service worker no longer takes a new build on its own: it
 * installs and then WAITS (public/service-worker.js, no skipWaiting at
 * install), so nothing reloads mid-practice. This module is the one flag that
 * says an update is waiting. It drives the red dot on the sigma (every
 * header) and on the drawer's Check For Updates item, and the "Update
 * Available!" prompt; the coach takes the update from either, which activates
 * the waiting worker and reloads (see checkForAppUpdate). Closing every tab
 * also activates it, so the next cold launch is on the new build regardless.
 *
 * A plain external store: it is raised by the dashboard's service-worker
 * registration before React renders, and read by components through
 * useSyncExternalStore.
 */

let pending = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** A new build is installed and waiting. Idempotent. */
export function markUpdatePending(): void {
  if (pending) return;
  pending = true;
  notify();
}

/** The waiting build has been taken (or is gone). Idempotent. */
export function clearUpdatePending(): void {
  // The update was taken (or is gone): a "Later" said to it no longer
  // applies, whether or not the flag was raised in this page.
  forgetUpdatePromptDismissed();
  if (!pending) return;
  pending = false;
  notify();
}

export function isUpdatePending(): boolean {
  return pending;
}

export function subscribeUpdatePending(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React view of the flag. */
export function usePendingUpdate(): boolean {
  return useSyncExternalStore(subscribeUpdatePending, isUpdatePending, () => false);
}

/**
 * Whether a registration already holds a built, waiting worker — an update
 * found in an earlier session that the coach never took. Checked at boot so
 * the dots come back after a reload that did not take the update.
 */
export function hasWaitingWorker(
  registration: { waiting?: unknown } | null | undefined
): boolean {
  return registration?.waiting != null;
}

/** The message the service worker listens for to take over. */
export const SKIP_WAITING_MESSAGE = { type: "SKIP_WAITING" } as const;
